"use client";

import { useTranslation } from "react-i18next";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { LayoutGrid, Columns } from "lucide-react";
import { POST_TYPES, POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";

interface PieceFiltersProps {
  status: string;
  onStatusChange: (status: string) => void;
  platform: string;
  onPlatformChange: (platform: string) => void;
  postType?: string;
  onPostTypeChange?: (postType: string) => void;
  viewMode: "gallery" | "kanban";
  onViewModeChange: (mode: "gallery" | "kanban") => void;
}

export function PieceFilters({
  status, onStatusChange, platform, onPlatformChange, postType = "ALL", onPostTypeChange, viewMode, onViewModeChange,
}: PieceFiltersProps) {
  const { t } = useTranslation("pieces");

  const STATUS_OPTIONS = [
    { value: "ALL", label: t("pieces:filters.status.ALL") },
    { value: "PENDING_REVIEW", label: t("pieces:filters.status.PENDING_REVIEW") },
    { value: "APPROVED", label: t("pieces:filters.status.APPROVED") },
    { value: "REJECTED", label: t("pieces:filters.status.REJECTED") },
    { value: "DRAFT", label: t("pieces:filters.status.DRAFT") },
  ];

  const PLATFORM_OPTIONS = [
    { value: "ALL", label: t("pieces:filters.platform.ALL") },
    { value: "INSTAGRAM", label: t("pieces:filters.platform.INSTAGRAM") },
    { value: "TIKTOK", label: t("pieces:filters.platform.TIKTOK") },
    { value: "FACEBOOK", label: t("pieces:filters.platform.FACEBOOK") },
    { value: "YOUTUBE", label: t("pieces:filters.platform.YOUTUBE") },
    { value: "LINKEDIN", label: t("pieces:filters.platform.LINKEDIN") },
    { value: "TWITTER", label: t("pieces:filters.platform.TWITTER") },
    { value: "THREADS", label: t("pieces:filters.platform.THREADS") },
    { value: "REDDIT", label: t("pieces:filters.platform.REDDIT") },
    { value: "GOOGLEBUSINESS", label: t("pieces:filters.platform.GOOGLEBUSINESS") },
  ];

  // Build the post-type picklist, grouped by tier (visually rendered flat).
  const postTypeOptions = [
    { value: "ALL", label: t("pieces:filters.postType.ALL") },
    ...POST_TYPES.map((pt: PostType) => ({
      value: pt,
      label: `${POST_TYPE_META[pt].platform} · ${POST_TYPE_META[pt].label}`,
    })),
  ];

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <Select value={status} onValueChange={onStatusChange}>
        <SelectTrigger className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={platform} onValueChange={onPlatformChange}>
        <SelectTrigger className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PLATFORM_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {onPostTypeChange && (
        <Select value={postType} onValueChange={onPostTypeChange}>
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {postTypeOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="ml-auto">
        <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && onViewModeChange(v as "gallery" | "kanban")}>
          <ToggleGroupItem value="gallery" aria-label={t("pieces:filters.galleryView")}>
            <LayoutGrid size={16} />
          </ToggleGroupItem>
          <ToggleGroupItem value="kanban" aria-label={t("pieces:filters.kanbanView")}>
            <Columns size={16} />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </div>
  );
}
